<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

// PageHeader — header padrao reutilizavel pra paginas do dashboard
// (ex: /settings/*, /studio/*, etc). Visual ClickUp-style: tile com
// icone accent-aware a esquerda + titulo + subtitle + slot de actions
// a direita; quando `backRoute` setado, mostra botao "Voltar" acima.
//
// Adaptado a partir de `StudioPageHeader.vue` — mesma API. O Studio
// continua usando seu proprio componente pra evitar risco de regressao
// nas 7+ paginas que o consomem; novas paginas devem preferir este.
const props = defineProps({
  // Lucide icon class (ex: 'i-lucide-settings'). Renderizado num box
  // tematico esquerda do titulo. Sem icone, o box some.
  icon: { type: String, default: '' },
  // Tom da paleta Radix-aware do projeto. Define cor do bg do icon box.
  accent: {
    type: String,
    default: 'slate',
    validator: v =>
      [
        'slate',
        'iris',
        'teal',
        'blue',
        'ruby',
        'amber',
        'orange',
        'cyan',
        'green',
        'gray',
      ].includes(v),
  },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  // Nome de rota Vue Router. Quando setado, mostra botao "Voltar"
  // acima do titulo. Omitir = sem botao voltar.
  backRoute: { type: String, default: '' },
  backLabel: { type: String, default: '' },
});

const { t, te } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

// Bg + foreground por accent — segue convencao Radix do CLAUDE.md
// (3 = bg sutil, 11 = texto contrastado).
const accentClasses = computed(
  () =>
    ({
      slate: 'bg-n-slate-3 text-n-slate-11',
      iris: 'bg-n-iris-3 text-n-iris-11',
      teal: 'bg-n-teal-3 text-n-teal-11',
      blue: 'bg-n-blue-3 text-n-blue-11',
      ruby: 'bg-n-ruby-3 text-n-ruby-11',
      amber: 'bg-n-amber-3 text-n-amber-11',
      orange: 'bg-n-orange-3 text-n-orange-11',
      cyan: 'bg-n-cyan-3 text-n-cyan-11',
      green: 'bg-n-green-3 text-n-green-11',
      gray: 'bg-n-gray-3 text-n-gray-11',
    })[props.accent]
);

const goBack = () => {
  if (!props.backRoute) return;
  router.push(accountScopedRoute(props.backRoute));
};

// Label do botao voltar: prop > nova chave LAYOUT.* (se existir) >
// chave legada do Studio (sempre presente em ambos locales).
const resolvedBackLabel = computed(() => {
  if (props.backLabel) return props.backLabel;
  if (te('LAYOUT.PAGE_HEADER.BACK')) return t('LAYOUT.PAGE_HEADER.BACK');
  return t('AI.STUDIO_PAGE_HEADER.BACK');
});
</script>

<template>
  <header
    class="shrink-0 bg-n-background pt-8 pb-7 border-b border-solid border-n-weak"
  >
    <!-- Outer header eh edge-to-edge (border-b corta a tela inteira).
         Conteudo interno (tile + titulo + actions) ganha px-6 lateral
         pra nao encostar na sidebar/borda. -->
    <button
      v-if="backRoute"
      type="button"
      class="inline-flex items-center gap-1.5 mb-3 ml-5 px-2 py-1 rounded text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1 hover:text-n-slate-12"
      @click="goBack"
    >
      <span class="i-lucide-arrow-left size-3.5" />
      {{ resolvedBackLabel }}
    </button>

    <div class="flex items-start justify-between gap-4 px-6">
      <div class="flex items-start gap-4 min-w-0">
        <div
          v-if="icon"
          class="shrink-0 flex items-center justify-center size-14 rounded-lg"
          :class="accentClasses"
        >
          <span class="size-7" :class="icon" />
        </div>
        <div class="min-w-0 flex-1">
          <h1 class="text-xl font-semibold text-n-slate-12 leading-tight">
            {{ title }}
          </h1>
          <p
            v-if="subtitle"
            class="mt-1 text-sm text-n-slate-11 leading-relaxed"
          >
            {{ subtitle }}
          </p>
        </div>
      </div>

      <div v-if="$slots.actions" class="shrink-0 flex items-center gap-2">
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>
